// X-One app registry — sprint MP7.
//
// Defines the suite (Resilience joined as app #7 in MP700, Orchestrate as
// app #8 in MP405, and the X-One AI Gateway console moved in by ADR 0030)
// + the per-app sidebar configuration. The `external` field on
// AppDefinition is kept for a product that lives on its own host; no
// entry uses it since ADR 0030.
//
// The AppLayout reads the current pathname, resolves which app context
// we're in, and renders that app's sidebar at the top — followed by
// the tenant-wide chrome (Identity, Integrations, Audit, etc.) which
// stays visible regardless of app.
//
// URL convention: each app gets a top-level prefix (`/aware`, `/xes`,
// `/posture`, `/surface`, `/exposure`, `/agents`, `/orchestrate`,
// `/insider`, `/resilience`, `/gateway`, `/ai-security`, `/umbra`).
// Tenant-wide chrome also lives at the root level (`/identity`,
// `/integrations`, `/audit`, `/policies`, `/settings`, `/profile`,
// `/help`). The
// legacy `/shell/*` URLs redirect to the new ones for backward compat.
//
// Why no /shell prefix anywhere: cleaner URLs + consistent shape.
// Customers and docs read `/aware/training` and immediately know it's
// the Aware app's training page. `/shell/training` was ambiguous.

import type { ComponentType } from 'react';

// ADR 0030 — `ai-gateway` is a suite peer: the Gateway console's tenant
// pages live in apps/web/src/pages/gateway/* at `/gateway/*`. (From MP801
// until ADR 0030 it was an external launcher tile opening the standalone
// console, `apps/ai-gateway`, on its own host. That app stays as a
// safety net until these pages are validated end to end.) Standalone
// Gateway becomes a hostname mode of this same tree (MP738), like
// Resilience below. The data plane (`services/ai-gateway`) is unchanged.
//
// MP700 — `resilience` IS a suite peer (app #7) and is registered the
// normal way: real rootPath, real entryRoute, populated sidebar, no
// `external` flag. Its pages live in apps/web/src/pages/resilience/*.
// It is ALSO sold standalone at its own domain, but that is a
// hostname-switched rendering of this same tree (MP738-MP743, see the
// standalone-mode seam in lib/tenantHost.ts) — not a separate SPA and
// not an external tile.
// MP405 — `orchestrate` is suite app #8: the MP254-MP265 orchestration
// surface promoted out of the Agents sidebar into its own front door
// (routes /orchestrate/*, entitlement + app flag `app.orchestrate`).
// Same in-`apps/web` treatment as Resilience — no separate SPA, no
// standalone host, no `external` flag.
export type AppKey =
  | 'aware'
  | 'xes'
  | 'posture'
  | 'surface'
  | 'exposure'
  | 'agents'
  | 'orchestrate'
  | 'insider'
  | 'resilience'
  | 'ai-gateway'
  | 'ai-security'
  | 'umbra';

export interface AppSidebarItem {
  to: string;
  label: string;
  /** Lazy import the icon component at use site (in AppLayout). */
  iconKey: string;
  /** When true, NavLink end={true} for exact-match active. */
  end?: boolean;
  /** MP58 — Optional nested sub-items. When present, the parent
   *  renders with a chevron + acts as a collapsible group. Sub-items
   *  render indented below when expanded. Auto-expanded when any
   *  child route is active so users always see their current
   *  position even after a hard refresh. */
  children?: AppSidebarItem[];
  /** MP316c — Optional platform feature-flag gate. When set, the
   *  nav item only renders for orgs whose resolved-flag set has this
   *  key with enabled=true. Missing flag = ungated (matches the
   *  launcher's `hiddenAppKeys` semantics — the platform team seeds
   *  flags at their own cadence, no forced-outage before rollout). */
  featureFlag?: string;
  /** Small heading shown above this row when it differs from the
   *  previous sibling's (used by generated sub-menus such as
   *  Insights → Dashboards). */
  group?: string;
  /** A shortcut to a page that belongs elsewhere (Insights →
   *  Dashboards lists Home, app overviews...). It never makes its group
   *  the current one, never auto-expands its parent and is not lit as
   *  the current page, so the page's own menu row stays the only one. */
  shortcut?: boolean;
  /** Short muted figure at the row's end ("12 on"). */
  hint?: string;
  /** Shown only to organization owners and admins (an app's Settings
   *  page, whose controls are admin-only). */
  adminOnly?: boolean;
}

export interface AppSidebarSection {
  title: string | null;
  items: AppSidebarItem[];
}

/** An app's main "create" action, shown as a button at the top of the
 *  app's menu (menu review, Oct 2026). Kept apart from `sidebar` so menu
 *  rows stay pages and the action looks like an action. */
export interface AppPrimaryAction {
  to: string;
  label: string;
  iconKey: string;
  /** Hidden when this flag is explicitly off, like sidebar rows. */
  featureFlag?: string;
  /** Shown only to organization owners and admins (the create pages
   *  behind these actions are admin-only). */
  adminOnly?: boolean;
}

export interface AppDefinition {
  key: AppKey;
  /** Short label used in the launcher tile + app switcher dropdown. */
  shortName: string;
  /** The app's own icon (a key in AppLayout's ICONS registry). Matches the
   *  icon on its Security overview tile, so one app looks the same
   *  everywhere it is listed. */
  iconKey: string;
  /** Display name in the page chrome + breadcrumb. */
  name: string;
  /** Marketing tagline shown on the launcher tile. */
  tagline: string;
  /** Entitlement key required for the org to access this app. */
  entitlementKey: string;
  /** SKU name for the locked-tile mailto link. */
  skuName: string;
  /** Top-level URL prefix. URLs inside this app start with this. */
  rootPath: string;
  /** Path the launcher tile + app switcher links to. Falls back to
   *  rootPath for shipped apps; null for "coming soon" apps. */
  entryRoute: string | null;
  /** Per-app sidebar sections rendered at the top of the chrome. */
  sidebar: AppSidebarSection[];
  primaryAction?: AppPrimaryAction;
  /** True when the app lives OUTSIDE `apps/web` — a standalone SPA on
   *  its own host with its own sign-in. The launcher tile opens
   *  `externalUrl` in a new tab instead of routing in-app, and the
   *  app is excluded from every in-app navigation surface (app
   *  switcher, apps aside, sidebar resolution, single-app fast-path).
   *  External apps always carry `entryRoute: null` + `sidebar: []`. */
  external?: boolean;
  /** Absolute URL of the external app's own host. Only meaningful
   *  when `external` is true. */
  externalUrl?: string;
}

export const APPS: AppDefinition[] = [
  {
    key: 'aware',
    iconKey: 'graduation-cap',
    shortName: 'Aware',
    name: 'X-One Aware',
    tagline: 'Training & awareness — phishing simulations, modules, and reporting.',
    entitlementKey: 'app.aware',
    skuName: 'Aware',
    rootPath: '/aware',
    entryRoute: '/aware',
    primaryAction: { to: '/aware/phishing/new-campaign', label: 'New campaign', iconKey: 'plus', adminOnly: true },
    sidebar: [
      {
        title: 'Aware',
        items: [
          { to: '/aware', label: 'Overview', iconKey: 'dashboard', end: true },
          {
            // Menu review (Oct 2026): grouped by task and opening on
            // Campaigns, the thing people run. "Template library" stays an
            // explicit child (MP89 feedback: without a named row the
            // library felt unreachable). "Reports" is renamed "Reported
            // emails" so it no longer clashes with organization Reports.
            // "New campaign" / "New template" are buttons on their list
            // pages now, not menu rows.
            to: '/aware/phishing/campaigns',
            label: 'Phishing',
            iconKey: 'shield-alert',
            children: [
              { to: '/aware/phishing/campaigns', label: 'Campaigns', iconKey: 'send' },
              { to: '/aware/phishing/campaign-templates', label: 'Campaign templates', iconKey: 'layout-template' },
              { to: '/aware/phishing/your-templates', label: 'Your templates', iconKey: 'file-text' },
              { to: '/aware/phishing', label: 'Template library', iconKey: 'library', end: true },
              { to: '/aware/phishing/reports', label: 'Reported emails', iconKey: 'flag' },
              { to: '/aware/phishing/analytics', label: 'Analytics', iconKey: 'activity' },
            ],
          },
          {
            // MP296 — Training sub-navigation collapses from sidebar
            // sub-items into horizontal Tabs on the page itself
            // (Dashboard / My modules / Assignments / Library). The
            // sidebar keeps a single Training entry so the shell stays
            // uncluttered; the tabs strip on the training page is the
            // sub-navigation. Legacy sub-URLs like /aware/training/
            // library and /aware/training/new-module still work — they
            // resolve to their respective tabs / views.
            to: '/aware/training',
            label: 'Training',
            iconKey: 'graduation-cap',
          },
          { to: '/aware/forum', label: 'Forum', iconKey: 'message-square' },
        ],
      },
    ],
  },
  {
    key: 'xes',
    iconKey: 'mail',
    shortName: 'XES',
    name: 'X-One Email Security',
    tagline: 'Inbound email scanning + AI phishing detection + quarantine.',
    entitlementKey: 'app.xes',
    skuName: 'XES',
    rootPath: '/xes',
    entryRoute: '/xes',
    sidebar: [
      {
        title: 'Email Security',
        items: [
          // MP318 — Dashboard leads the sidebar so admins land on
          // trend context before jumping into the queue.
          { to: '/xes', label: 'Triage queue', iconKey: 'mail', end: true },
          { to: '/xes/dashboard', label: 'Overview', iconKey: 'bar-chart-3' },
          // MP316c — Rules library gated behind the `xes.rules` flag.
          // Seeded state=on so the item is visible for every tenant on
          // rollout; platform can flip to beta/off from platform-console
          // without a deploy (used for staged rollouts of MP316's
          // attachment-analysis predicates + MP321's test harness).
          { to: '/xes/rules', label: 'Rules', iconKey: 'shield-check', featureFlag: 'xes.rules' },
          // MP318b — Settings page (connectors, verdict guidance,
          // auto-assign training). Split out from the triage page.
          { to: '/xes/settings', label: 'Settings', iconKey: 'settings' },
        ],
      },
    ],
  },
  {
    key: 'posture',
    iconKey: 'laptop',
    shortName: 'Posture',
    name: 'X-One Posture',
    tagline: 'Device posture compliance from your MDM/EDR stack.',
    entitlementKey: 'app.posture',
    skuName: 'Posture',
    rootPath: '/posture',
    // Menu review (Oct 2026): opens on the dashboard, the first menu row.
    entryRoute: '/posture/overview',
    // Menu review (Oct 2026): IA1.5 moved Posture's navigation into an
    // in-page icon rail plus per-area sub-menus inside PostureShell, and
    // left a single row here so the menu never appeared twice. That made
    // three navigation layers with engineering labels (IaaS, IaC, Hyper,
    // Hub), and the rail was hidden below 768px, so phones had no Posture
    // navigation at all. The rail is gone; everything lives here once,
    // in plain words, and reaches phones through the sidebar drawer.
    sidebar: [
      {
        title: 'Posture',
        items: [
          { to: '/posture/overview', label: 'Overview', iconKey: 'dashboard' },
          { to: '/posture', label: 'All areas', iconKey: 'layout-grid', end: true },
        ],
      },
      {
        title: 'Investigate',
        items: [
          { to: '/posture/alerts', label: 'Alerts', iconKey: 'alert-triangle' },
          { to: '/posture/cases', label: 'Cases', iconKey: 'folder-open' },
          { to: '/posture/attack-graph', label: 'Attack graph', iconKey: 'network' },
          { to: '/posture/exemptions', label: 'Exemptions', iconKey: 'shield-off' },
        ],
      },
      {
        title: 'Areas',
        items: [
          { to: '/posture/endpoint', label: 'Devices', iconKey: 'laptop' },
          {
            to: '/posture/iaas',
            label: 'Cloud',
            iconKey: 'cloud',
            children: [
              { to: '/posture/iaas', label: 'Overview', iconKey: 'gauge', end: true },
              { to: '/posture/iaas/findings', label: 'Findings', iconKey: 'alert-triangle' },
              { to: '/posture/iaas/compliance', label: 'Compliance', iconKey: 'shield-check' },
              { to: '/posture/iaas/baselines', label: 'Baselines', iconKey: 'clipboard-list' },
              { to: '/posture/iaas/unified', label: 'Resources', iconKey: 'layout-grid' },
              { to: '/posture/iaas/scans', label: 'Scans', iconKey: 'refresh-cw' },
              { to: '/posture/iaas/checks', label: 'Checks', iconKey: 'clipboard-list' },
            ],
          },
          {
            to: '/posture/iac',
            label: 'Infrastructure code',
            iconKey: 'file-code',
            children: [
              { to: '/posture/iac', label: 'Overview', iconKey: 'gauge', end: true },
              { to: '/posture/iac/findings', label: 'Findings', iconKey: 'alert-triangle' },
              { to: '/posture/iac/unified', label: 'Resources', iconKey: 'layout-grid' },
              { to: '/posture/iac/scans', label: 'Scans', iconKey: 'refresh-cw' },
              { to: '/posture/iac/checks', label: 'Checks', iconKey: 'clipboard-list' },
            ],
          },
          {
            to: '/posture/saas',
            label: 'SaaS',
            iconKey: 'package',
            children: [
              { to: '/posture/saas', label: 'Overview', iconKey: 'gauge', end: true },
              { to: '/posture/saas/findings', label: 'Findings', iconKey: 'alert-triangle' },
              { to: '/posture/saas/compliance', label: 'Compliance', iconKey: 'shield-check' },
              { to: '/posture/saas/unified', label: 'Resources', iconKey: 'layout-grid' },
              { to: '/posture/saas/scans', label: 'Scans', iconKey: 'refresh-cw' },
              { to: '/posture/saas/checks', label: 'Checks', iconKey: 'clipboard-list' },
            ],
          },
          {
            to: '/posture/hypervisor',
            label: 'Virtualization',
            iconKey: 'server',
            children: [
              { to: '/posture/hypervisor', label: 'Overview', iconKey: 'gauge', end: true },
              { to: '/posture/hypervisor/findings', label: 'Findings', iconKey: 'alert-triangle' },
              { to: '/posture/hypervisor/compliance', label: 'Compliance', iconKey: 'shield-check' },
              { to: '/posture/hypervisor/unified', label: 'Resources', iconKey: 'layout-grid' },
              { to: '/posture/hypervisor/scans', label: 'Scans', iconKey: 'refresh-cw' },
              { to: '/posture/hypervisor/checks', label: 'Checks', iconKey: 'clipboard-list' },
            ],
          },
        ],
      },
      {
        title: 'Manage',
        items: [
          { to: '/posture/policies', label: 'Policies', iconKey: 'lock' },
          { to: '/posture/agents', label: 'Posture agents', iconKey: 'bot' },
          { to: '/posture/cloud-accounts', label: 'Connected accounts', iconKey: 'key-round' },
          { to: '/posture/outbound', label: 'Alert routing', iconKey: 'plug' },
          { to: '/posture/settings', label: 'Settings', iconKey: 'settings' },
        ],
      },
    ],
  },
  {
    key: 'surface',
    iconKey: 'shield-alert',
    shortName: 'Surface',
    name: 'X-One Surface',
    tagline: 'External attack surface monitoring + threat-intel findings.',
    entitlementKey: 'app.surface',
    skuName: 'Surface',
    rootPath: '/surface',
    entryRoute: '/surface',
    sidebar: [
      {
        title: 'Surface',
        // MP98 — Surface gains an Assets inventory sub-item alongside
        // the existing External risk findings page.
        // MP101 — Internal risk is its own TOP-LEVEL sidebar item
        // alongside External risk (not nested under it). The two
        // risk surfaces are peer concerns (outside-the-perimeter
        // vs inside-the-tenant); nesting one under the other would
        // misframe them as parent/child rather than peer.
        items: [
          { to: '/surface', label: 'Overview', iconKey: 'gauge', end: true },
          {
            to: '/surface/findings',
            label: 'External risk',
            iconKey: 'globe',
            end: true,
            children: [
              { to: '/surface/findings', label: 'Findings', iconKey: 'shield-alert', end: true },
              { to: '/surface/assets', label: 'Internet-facing assets', iconKey: 'globe' },
              { to: '/surface/analytics', label: 'Attack surface analytics', iconKey: 'bar-chart-3', featureFlag: 'surface.uamReads' },
            ],
          },
          {
            to: '/surface/internal',
            label: 'Internal risk',
            iconKey: 'users',
            end: true,
            // MP102 — Sub-menu surfaces the AD & Entra ID check
            // suite explicitly. As future internal-risk sources land
            // (Okta, Google Workspace, on-prem AD), they each get
            // their own peer sub-item here.
            children: [
              { to: '/surface/internal', label: 'Overview', iconKey: 'users', end: true },
              { to: '/surface/internal/entra', label: 'AD & Entra ID risk', iconKey: 'shield-alert' },
            ],
          },
          // MP606 — the Unified Asset Model (MP601-MP605) gets its first
          // pages. A third top-level peer, not nested under External
          // risk: UAM spans device / cloud / identity / SaaS / software /
          // network / web / OT, and the EASM inventory at /surface/assets
          // is one future facet of it, not its parent. Each row's
          // `featureFlag` is the flag that gates its API — the Preview
          // chip and the hide-when-off behaviour follow from that alone.
          {
            to: '/surface/inventory',
            label: 'Inventory',
            iconKey: 'layout-grid',
            end: true,
            featureFlag: 'surface.queryViews',
            children: [
              { to: '/surface/inventory', label: 'All assets', iconKey: 'layout-grid', end: true, featureFlag: 'surface.queryViews' },
              // MP941 — asset groups (rules or hand-picked) + inherited criticality.
              { to: '/surface/queries', label: 'Queries', iconKey: 'search', featureFlag: 'surface.queryViews' },
              { to: '/surface/groups', label: 'Asset groups', iconKey: 'folder-open', featureFlag: 'surface.queryViews' },
              { to: '/surface/sources', label: 'Sources', iconKey: 'refresh-cw', featureFlag: 'surface.uamReads' },
              // MP664 — connect GitHub / GitLab / Bitbucket / Azure DevOps.
              { to: '/surface/repositories', label: 'Code repositories', iconKey: 'file-code', featureFlag: 'surface.uamReads' },
              // MP946 — compare the inventory with each connected CMDB.
              { to: '/surface/cmdb-reconcile', label: 'CMDB reconciliation', iconKey: 'clipboard-list', featureFlag: 'surface.uamReads' },
              { to: '/surface/graph', label: 'Relationship graph', iconKey: 'network', featureFlag: 'surface.assetGraph' },
              // MP438 — SBOM upload, component graph, OSS enrichment.
              { to: '/surface/sboms', label: 'SBOMs', iconKey: 'file-json', featureFlag: 'surface.sbom' },
            ],
          },
          // Scanning, sync schedules, CMDB updates and SBOM upload tokens
          // in one place, instead of scattered over the working pages.
          { to: '/surface/settings', label: 'Settings', iconKey: 'settings', adminOnly: true },
        ],
      },
    ],
  },
  {
    // MP900 — X-One Exposure: one place to see and work every
    // vulnerability and exposure, scored by what attackers actually use
    // (docs/programs/x-one-exposure-brinqa-axonius.md). Per ADR 0029 it
    // lives inside apps/web like every suite app. The Vulnerabilities
    // dashboard (Overview) and its Findings workbench moved in from
    // /dashboards/vulnerabilities*, which now redirect here. Later
    // sections (Horizon emerging threats, Explorer, Policies, Sources,
    // Reports) join the menu as they are built, never as placeholders.
    key: 'exposure',
    iconKey: 'radar',
    shortName: 'Exposure',
    name: 'X-One Exposure',
    tagline: 'Every vulnerability across your devices and assets in one list, ranked by what attackers are actually using.',
    entitlementKey: 'app.exposure',
    skuName: 'Exposure',
    rootPath: '/exposure',
    entryRoute: '/exposure',
    sidebar: [
      {
        title: 'Exposure',
        items: [
          { to: '/exposure', label: 'Overview', iconKey: 'dashboard', end: true },
          { to: '/exposure/risk', label: 'Risk overview', iconKey: 'bar-chart-3' },
          { to: '/exposure/intel', label: 'Threat intel', iconKey: 'zap' },
          { to: '/exposure/findings', label: 'Findings', iconKey: 'alert-triangle' },
          { to: '/exposure/fix-plans', label: 'Fix plans', iconKey: 'wrench' },
          { to: '/exposure/groups', label: 'Groups', iconKey: 'folder-open' },
          { to: '/exposure/exceptions', label: 'Exceptions', iconKey: 'shield-off' },
          { to: '/exposure/sla', label: 'SLAs', iconKey: 'gauge' },
          { to: '/exposure/compliance', label: 'Compliance', iconKey: 'shield-check' },
          { to: '/exposure/reports', label: 'Reports', iconKey: 'file-text' },
          { to: '/exposure/baselines', label: 'Baselines', iconKey: 'clipboard-list' },
          { to: '/exposure/queries', label: 'Queries', iconKey: 'search' },
          { to: '/exposure/settings', label: 'Settings', iconKey: 'settings' },
        ],
      },
    ],
  },
  {
    key: 'agents',
    iconKey: 'bot',
    shortName: 'Agents',
    name: 'X-One Agents',
    tagline: 'Autonomous response — install or author agents on the runtime.',
    entitlementKey: 'app.agents',
    skuName: 'Agents',
    rootPath: '/agents',
    entryRoute: '/agents',
    primaryAction: { to: '/agents/new', label: 'New agent', iconKey: 'plus', featureFlag: 'agents.custom', adminOnly: true },
    sidebar: [
      {
        title: 'Agents',
        // MP56 → MP57 — three surfaces, not one. MP56 introduced
        // the Installed + Build-custom split; MP57 inserted a
        // dedicated Catalog page between them.
        // MP94 — "Library" added as a parallel surface backed by
        // the platform agent library (vs Catalog's hardcoded
        // AGENT_TEMPLATES TS array).
        // MP96 — Catalog dropped. Library is the canonical source
        // of agent templates now (the hardcoded entries got
        // imported into the library via the Platform admin's
        // "Import from catalog" button). Three sidebar items now:
        // Installed (running agents), Library (browse + clone),
        // Build custom (author from scratch).
        // Oct 2026 — Overview is now the app's landing page and the list
        // moved to /agents/installed. Run history is /agents/activity,
        // opened from Overview and each installed agent.
        items: [
          { to: '/agents', label: 'Overview', iconKey: 'dashboard', end: true },
          { to: '/agents/installed', label: 'Installed', iconKey: 'bot' },
          { to: '/agents/library', label: 'Library', iconKey: 'library' },
          // MP266 — Write-class tool calls from Athena / agents
          // flagged approvalRequired land here for admin review.
          { to: '/agents/approvals', label: 'Approvals', iconKey: 'shield-check' },
          // MP405 — Orchestration moved to its own suite app (X-One
          // Orchestrate, /orchestrate/*). This row stays as an outbound
          // link so existing muscle memory still lands somewhere; the
          // old /agents/orchestration* URLs redirect there too.
          { to: '/orchestrate', label: 'Orchestration ↗', iconKey: 'network', featureFlag: 'app.orchestrate' },
        ],
      },
    ],
  },
  {
    // MP405 — X-One Orchestrate, suite app #8. The orchestration
    // product (MP254-MP265: first-class `orchestrations` entity, canvas
    // editor, Athena natural-language builder, materialise /
    // un-materialise, analytics, runs, precise attribution) had no
    // front door — it was the 4th sidebar item under Agents, and
    // `app.agents` was MSSP-only, so non-MSSP tenants could never reach
    // it. Promoted for the 2026-09-27 beta.
    //
    // Packaging only: no new capabilities, the API stays at
    // /v1/orgs/:orgId/orchestrations* (app-neutral), Firestore
    // collection unchanged. Orchestrations compose INSTALLED agents, so
    // the second sidebar section cross-links into the Agents app rather
    // than hiding the dependency. Placed directly after `agents` so
    // the launcher grid reads Agents → Orchestrate.
    key: 'orchestrate',
    iconKey: 'network',
    shortName: 'Orchestrate',
    name: 'X-One Orchestrate',
    tagline: 'Compose installed agents into signal-routed workflows — build in natural language with Athena, materialise, and watch every run.',
    entitlementKey: 'app.orchestrate',
    skuName: 'Orchestrate',
    rootPath: '/orchestrate',
    entryRoute: '/orchestrate',
    primaryAction: { to: '/orchestrate/new', label: 'New workflow', iconKey: 'plus', featureFlag: 'app.orchestrate', adminOnly: true },
    sidebar: [
      {
        title: 'Orchestrate',
        items: [
          { to: '/orchestrate', label: 'Workflows', iconKey: 'network', end: true, featureFlag: 'app.orchestrate' },
          { to: '/orchestrate/templates', label: 'Templates', iconKey: 'layout-template', featureFlag: 'app.orchestrate' },
        ],
      },
      {
        // Cross-links, not owned surfaces: a workflow is only as useful
        // as the agents installed to run in it, and approvals are where
        // Athena's write-class tool calls land. Titled as a link to
        // another app so it doesn't read as a duplicate menu.
        title: 'Related in Agents',
        items: [
          { to: '/agents/installed', label: 'Installed agents', iconKey: 'bot' },
          { to: '/agents/approvals', label: 'Approvals', iconKey: 'shield-check' },
        ],
      },
    ],
  },
  {
    key: 'insider',
    iconKey: 'users',
    shortName: 'Insider',
    name: 'X-One Insider',
    tagline: 'User behaviour analytics + insider risk scoring.',
    entitlementKey: 'app.insider',
    skuName: 'Insider',
    rootPath: '/insider',
    entryRoute: '/insider',
    sidebar: [
      {
        title: 'Insider',
        items: [
          { to: '/insider', label: 'Events', iconKey: 'users', end: true, featureFlag: 'insider.events' },
          { to: '/insider/anomalies', label: 'Unusual behaviour', iconKey: 'activity', featureFlag: 'insider.events' },
        ],
      },
    ],
  },
  {
    // MP700 — X-One Resilience, suite app #7. Cyber resilience as one
    // discipline: immutable backup + recovery, identity forest defence
    // and recovery, and business-continuity orchestration under a
    // single control plane (see
    // docs/programs/x-one-resilience-app-scoping.md).
    //
    // A NORMAL suite entry, deliberately: real rootPath, real
    // entryRoute, populated sidebar, no `external` flag. Resilience is
    // also sold standalone at its own domain, but that is the same
    // React tree rendered with different chrome per hostname
    // (MP738-MP743), not a second SPA. A tab-jump out of the launcher would be a visibly
    // worse experience than the six in-place peers above.
    key: 'resilience',
    iconKey: 'archive',
    shortName: 'Resilience',
    name: 'X-One Resilience',
    tagline: 'Immutable backup, identity forest recovery, and continuous recovery drills.',
    entitlementKey: 'app.resilience',
    skuName: 'Resilience',
    rootPath: '/resilience',
    entryRoute: '/resilience',
    sidebar: [
      {
        title: 'Resilience',
        // Flat, seven items — the shape the scoping doc's product
        // surface implies. As the arc lands (MP717-MP722 identity,
        // MP729-MP731 DSPM) some of these will grow children the way
        // Aware's Phishing entry did; keep the flat shape until a
        // section genuinely has sub-surfaces rather than pre-nesting.
        items: [
          // MP316g — every customer Resilience page sits behind the
          // `resilience.tenantSurface` flag (server-gated since MP316e/f).
          { to: '/resilience', label: 'Overview', iconKey: 'dashboard', end: true, featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/assets', label: 'Protected assets', iconKey: 'server', featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/snapshots', label: 'Snapshots', iconKey: 'archive', featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/plans', label: 'Recovery plans', iconKey: 'clipboard-list', featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/dependencies', label: 'Dependencies', iconKey: 'server', featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/drills', label: 'Drills', iconKey: 'shield-check', featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/vault', label: 'Vault', iconKey: 'lock', featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/evidence', label: 'Evidence', iconKey: 'file-text', featureFlag: 'resilience.tenantSurface' },
          { to: '/resilience/settings', label: 'Settings', iconKey: 'settings', featureFlag: 'resilience.tenantSurface' },
        ],
      },
    ],
  },
  {
    // ADR 0030 — X-One AI Gateway's console, a normal suite entry like
    // Resilience: the thirteen pages of the standalone console, on the
    // platform's chrome, session and dark mode. Gate: the
    // `app.ai-gateway` entitlement (the pages explain when it is off)
    // plus the api's own `aiGateway.keys` flag on every route.
    key: 'ai-gateway',
    iconKey: 'sparkles',
    shortName: 'AI Gateway',
    name: 'X-One AI Gateway',
    tagline: 'Route, govern, and observe your organization’s AI traffic.',
    entitlementKey: 'app.ai-gateway',
    skuName: 'AI Gateway',
    rootPath: '/gateway',
    entryRoute: '/gateway',
    sidebar: [
      {
        title: 'AI Gateway',
        items: [
          { to: '/gateway', label: 'Overview', iconKey: 'dashboard', end: true },
          { to: '/gateway/security', label: 'AI Security', iconKey: 'shield-alert' },
          { to: '/gateway/providers', label: 'Providers', iconKey: 'cloud' },
          { to: '/gateway/routes', label: 'Routes', iconKey: 'network' },
          { to: '/gateway/policies', label: 'Policies', iconKey: 'shield-check' },
          { to: '/gateway/requests', label: 'Requests', iconKey: 'activity' },
          { to: '/gateway/approvals', label: 'Approvals', iconKey: 'clipboard-list' },
          { to: '/gateway/loops', label: 'Loops', iconKey: 'refresh-cw' },
          { to: '/gateway/usage', label: 'Usage', iconKey: 'bar-chart-3' },
          { to: '/gateway/billing', label: 'Billing', iconKey: 'gauge' },
          { to: '/gateway/health', label: 'Health', iconKey: 'server' },
          { to: '/gateway/keys', label: 'API keys', iconKey: 'key-round' },
          { to: '/gateway/identities', label: 'Identities', iconKey: 'log-in' },
          { to: '/gateway/settings', label: 'Settings', iconKey: 'settings' },
        ],
      },
    ],
  },
  {
    // X-One AI Security: what AI in the organization tried to do, and what
    // stopped it. Its own suite app and entitlement, decided 1 Nov 2026
    // (docs/programs/x-one-ai-security.md), sellable without the Gateway;
    // bundled on Enterprise like Exposure. Stage 1 reads Gateway traffic,
    // so its pages are the Gateway's AI Security pages in this app's chrome
    // (pages/aiSecurity), and the Gateway keeps its own copy at
    // /gateway/security. The inventory (Stage 2) and response (Stage 3)
    // join the menu as they are built, never as placeholders.
    key: 'ai-security',
    iconKey: 'scan-eye',
    shortName: 'AI Security',
    name: 'X-One AI Security',
    tagline: 'See what AI in your organization tried to do, and what stopped it.',
    entitlementKey: 'app.ai-security',
    skuName: 'AI Security',
    rootPath: '/ai-security',
    entryRoute: '/ai-security',
    sidebar: [
      {
        title: 'AI Security',
        items: [
          { to: '/ai-security', label: 'Overview', iconKey: 'dashboard', end: true },
          { to: '/ai-security/events', label: 'Flagged events', iconKey: 'shield-alert' },
          { to: '/ai-security/permissions', label: 'Permissions', iconKey: 'shield-check' },
        ],
      },
    ],
  },
  {
    // MP480 — X-One Umbra: keeps management interfaces and remote access
    // off the internet, reachable only by verified people on trusted
    // devices. A normal suite entry (no `external` flag): its customer
    // screens are native pages under /umbra/*, like Surface (ADR 0029)
    // and the AI Gateway console (ADR 0030). Overview and Settings
    // (MP481), Actions (MP483), Events (MP484), and the read screens
    // (MP485); screens join the menu as they are built, never as
    // placeholders.
    key: 'umbra',
    iconKey: 'lock',
    shortName: 'Umbra',
    name: 'X-One Umbra',
    tagline: 'Hide your management interfaces and remote access from the internet, so only verified people on trusted devices can reach them.',
    entitlementKey: 'app.umbra',
    skuName: 'Umbra',
    rootPath: '/umbra',
    entryRoute: '/umbra',
    sidebar: [
      {
        title: 'Umbra',
        items: [
          { to: '/umbra', label: 'Overview', iconKey: 'dashboard', end: true },
          // MP485 — native read screens. Sessions carry the revoke /
          // quarantine buttons (analyst+, catalogue-permitted); policy
          // is read-only (changes are signed in Umbra's own console).
          { to: '/umbra/sessions', label: 'Sessions', iconKey: 'activity' },
          { to: '/umbra/exposure', label: 'Exposure', iconKey: 'globe' },
          { to: '/umbra/policy', label: 'Policy', iconKey: 'file-text' },
          { to: '/umbra/connectors', label: 'Connectors', iconKey: 'plug' },
          // MP483 — enforcement actions (viewers see them; analysts+
          // request; admins approve — the api checks).
          { to: '/umbra/actions', label: 'Actions', iconKey: 'shield-alert' },
          // MP484 — telemetry events (decoy hits, failed knocks, action
          // results); owners can send a test event.
          { to: '/umbra/events', label: 'Events', iconKey: 'radar' },
          // MP481 — everyone with an Umbra role sees the settings; only
          // owners can change them (the api checks).
          { to: '/umbra/settings', label: 'Settings', iconKey: 'settings' },
        ],
      },
    ],
  },
];

const APP_BY_KEY: Record<AppKey, AppDefinition> = Object.fromEntries(
  APPS.map((a) => [a.key, a]),
) as Record<AppKey, AppDefinition>;

export function getApp(key: AppKey): AppDefinition {
  return APP_BY_KEY[key];
}

/**
 * Resolve which app context applies to a given pathname. App URLs
 * have a top-level prefix (`/aware/...`); tenant-wide URLs
 * (`/identity/*`, `/integrations/*`, `/audit`, etc.) return null —
 * AppLayout in that case shows only the tenant-wide chrome with no
 * app-specific sidebar section.
 *
 * Matching is first-wins over APPS in declaration order, so two app
 * rootPaths must never prefix one another. `/resilience` (MP700) and
 * `/orchestrate` (MP405) share no prefix with the six original apps.
 *
 * Member risk lives under `/aware/risk` for now (Aware-flavoured),
 * but the URL is open for cross-cutting access if customers ask.
 *
 * External apps are skipped explicitly (none since ADR 0030 moved the AI
 * Gateway console in): an app with no in-`apps/web` routes must never
 * become `currentApp` — that would hand AppLayout an app whose sidebar
 * is `[]`.
 */
export function resolveAppFromPathname(pathname: string): AppDefinition | null {
  for (const app of APPS) {
    if (app.external) continue;
    if (pathname === app.rootPath || pathname.startsWith(app.rootPath + '/')) {
      return app;
    }
  }
  return null;
}

/** Tenant-wide sidebar sections shown below the per-app section in
 *  every page that lives in an app shell. Cross-cutting concerns —
 *  identity / integrations / audit / org settings — that need to be
 *  accessible no matter which app the admin is in. */
export interface TenantSidebarConfig {
  /** Home, alone at the top. The Apps toggle is rendered right after it
   *  by AppLayout (data-driven from APPS + entitlements). */
  overview: AppSidebarSection;
  /** Menu review (Oct 2026): "what is happening" in one place — member
   *  risk, the AI digest, reports and KPIs (was split between the top
   *  group and a Reporting row). */
  insights: AppSidebarSection;
  /** Starts the "Administration" group: people and access. */
  identity: AppSidebarSection;
  integrations: AppSidebarSection;
  /** Policies, the audit log and organization settings. */
  organization: AppSidebarSection;
  platform: AppSidebarSection;
  help: AppSidebarSection;
}

// Menu review (Oct 2026). Same mechanics as MP121/MP122 — each group is
// one parent row whose children open in the MP67 tenant aside, and the
// parent's `to` is its first child's route — regrouped by what people
// come to do:
//   Home · Apps · Insights · Administration (People & access,
//   Integrations, Organization) · MSSP · Platform · Help
// Entitlement and flag gates (AI Insights, Audit) are applied by
// AppLayout's gateTenantItems to BOTH the main sidebar and the tenant
// aside, so a gated row can never surface through either.
// Create actions (New KPI / New dashboard) are buttons on the KPI page,
// not menu rows.
export const TENANT_SIDEBAR: TenantSidebarConfig = {
  overview: {
    title: null,
    items: [{ to: '/launcher', label: 'Home', iconKey: 'home', end: true }],
  },
  insights: {
    title: null,
    items: [
      // Policies sits right after Apps: it covers every app.
      {
        to: '/policies',
        label: 'Policies',
        iconKey: 'shield',
        // AppLayout fills the side panel from the org's rule sets
        // (lib/usePolicyMenu.ts): All policies, then each rule set under
        // its category. This static row is the fallback before that loads.
        children: [{ to: '/policies', label: 'All policies', iconKey: 'shield', end: true }],
      },
      // Shortcuts to every setting across the apps. Spans the suite like
      // Policies, so it shares this section's standalone handling.
      {
        to: '/configuration',
        label: 'Configuration',
        iconKey: 'settings',
        // AppLayout fills the side panel from the hub's cards
        // (lib/configurationMenu.ts): All configuration, then each card
        // the person can use under its category. This file can't import
        // them, because the hub's catalog imports this one.
        children: [{ to: '/configuration', label: 'All configuration', iconKey: 'settings', end: true }],
      },
      {
        to: '/dashboards',
        label: 'Insights',
        iconKey: 'bar-chart-3',
        children: [
          { to: '/dashboards', label: 'Dashboards', iconKey: 'dashboard' },
          { to: '/risk', label: 'Member risk', iconKey: 'gauge' },
          // MP316c — AI Insights is gated by the `aiInsights` entitlement
          // (the org paid for it) AND the `ai.insights` flag (progressive
          // rollout; seeded beta, opted into under Settings → Beta features).
          { to: '/insights', label: 'AI Insights', iconKey: 'sparkles', featureFlag: 'ai.insights' },
          { to: '/reports', label: 'Reports', iconKey: 'file-text' },
          { to: '/kpi', label: 'KPIs', iconKey: 'layout-grid' },
        ],
      },
    ],
  },
  identity: {
    title: 'Administration',
    items: [
      {
        to: '/identity/members',
        label: 'People & access',
        iconKey: 'users',
        children: [
          { to: '/identity/members', label: 'Members & invitations', iconKey: 'users' },
          { to: '/identity/sso', label: 'Single sign-on', iconKey: 'log-in' },
          { to: '/identity/scim', label: 'SCIM provisioning', iconKey: 'refresh-cw' },
          { to: '/identity/mfa', label: 'MFA policy', iconKey: 'lock' },
          { to: '/identity/email-domains', label: 'Email domains', iconKey: 'at-sign' },
        ],
      },
    ],
  },
  integrations: {
    title: null,
    items: [
      {
        to: '/integrations/connectors',
        label: 'Integrations',
        iconKey: 'zap',
        children: [
          { to: '/integrations/connectors', label: 'Connectors', iconKey: 'zap' },
          { to: '/integrations/webhooks', label: 'Webhooks', iconKey: 'send' },
          { to: '/integrations/credentials', label: 'OAuth credentials', iconKey: 'key-round' },
        ],
      },
    ],
  },
  organization: {
    title: null,
    items: [
      {
        to: '/audit',
        label: 'Organization',
        iconKey: 'building-2',
        // One level, one name per page: the old tree had Policies ▸
        // Settings beside organization Settings — two rows called
        // "Settings" — and Audit sat under Reporting.
        children: [
          { to: '/audit', label: 'Audit log', iconKey: 'activity' },
          { to: '/settings', label: 'Organization settings', iconKey: 'settings' },
        ],
      },
    ],
  },
  platform: {
    title: null,
    items: [
      {
        to: '/shell/platform',
        label: 'Platform',
        iconKey: 'shield-check',
        children: [
          { to: '/shell/platform', label: 'Overview', iconKey: 'shield', end: true },
          { to: '/shell/platform/admins', label: 'Admins', iconKey: 'user-circle' },
          { to: '/shell/platform/library', label: 'Phishing library', iconKey: 'library' },
          // MP90 — Training module library (parallel to phishing library).
          { to: '/shell/platform/training-library', label: 'Training library', iconKey: 'graduation-cap' },
          // MP94 — Agent library (parallel to phishing + training libraries).
          { to: '/shell/platform/agent-library', label: 'Agent library', iconKey: 'bot' },
          { to: '/shell/platform/audit', label: 'Audit', iconKey: 'activity' },
        ],
      },
    ],
  },
  help: {
    title: 'Help',
    items: [
      { to: '/help', label: 'Documentation', iconKey: 'book-open' },
      // The setup guide was only linked from the Home banner, so it
      // became unreachable once that banner was dismissed.
      { to: '/onboarding', label: 'Getting started', iconKey: 'flag' },
    ],
  },
};

export type IconKey = string;
export type IconRegistry = Record<IconKey, ComponentType<{ className?: string }>>;
